import React, { useEffect, useId, useRef } from "react";
import { Button, Modal } from "../../components/common";

interface ActivationConfirmDialogProps {
  open: boolean;
  scope: "selected" | "all";
  count: number;
  organisationName: string;
  disabled?: boolean;
  filteredCount?: number;
  stagedNotPermitted?: number;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function ActivationConfirmDialog({
  open, scope, count, organisationName, disabled = false, filteredCount, stagedNotPermitted = 0, onCancel, onConfirm,
}: ActivationConfirmDialogProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    // This dialog has only Cancel and Confirm; keep focus on enabled actions.
    const actions = () => Array.from(dialog.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"));
    const focusCancel = () => actions()[0]?.focus();
    const containFocus = (event: FocusEvent) => {
      if (!dialog.contains(event.target as Node)) {
        event.stopPropagation();
        focusCancel();
      }
    };
    const containKeyboard = (event: KeyboardEvent) => {
      if (!dialog.contains(event.target as Node)) {
        event.preventDefault();
        event.stopPropagation();
        focusCancel();
        if (event.key === "Escape") cancelRef.current();
        return;
      }
      if (event.key === "Tab") {
        const buttons = actions();
        const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
        event.preventDefault();
        buttons[(index + (event.shiftKey ? buttons.length - 1 : 1)) % buttons.length]?.focus();
      }
    };
    document.addEventListener("focusin", containFocus, true);
    document.addEventListener("keydown", containKeyboard, true);
    focusCancel();
    return () => {
      document.removeEventListener("focusin", containFocus, true);
      document.removeEventListener("keydown", containKeyboard, true);
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [open]);
  return (
    <Modal activeModal={open} onClose={onCancel} hideHeader noFade
      dialogClass="w-full max-w-lg mx-4">
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId} className="text-xl font-semibold mb-4">
          {scope === "selected" ? "Activate selected staff?" : "Activate all permitted staged staff?"}
        </h2>
        <p className="text-sm text-gray-700 mb-6">
          {scope === "selected"
            ? `This sends an invitation email to the ${count} selected staff on this page. Staff on other pages are not included. Emails already sent cannot be recalled.`
            : `This sends invitation emails to all ${count} staged staff in ${organisationName} that your role can activate — every page of the list, not only what you can see. It runs in batches of 25 from this browser tab; keep the tab open until it finishes. Emails already sent cannot be recalled.`}
        </p>
        {scope === "all" && filteredCount !== undefined && <p className="text-sm text-gray-700 mb-4">Your current search or filter shows {filteredCount} staff; this action is not limited to them.</p>}
        {scope === "all" && stagedNotPermitted > 0 && <p className="text-sm text-gray-700 mb-4">{stagedNotPermitted} staged staff cannot be activated by your role and are not included.</p>}
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button disabled={disabled || count === 0} onClick={onConfirm}>Activate {count} staff</Button>
        </div>
      </section>
    </Modal>
  );
}
